import type { B2BBuyer } from '../types/b2b';
import type { Reservation } from '../types';
import { getValidAuthHeadersOrThrow } from './auth';

interface B2BReservation extends Reservation {
  pickup_scheduled_date?: string;
  reservation_source?: string;
  buyer_id?: string;
}

interface ReservationItemForEmail {
  name: string;
  sku?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface SupplierInfo {
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  vat_number?: string;
}

export interface EmailAttachment {
  filename: string;
  // Either pass `content` (base64-encoded) inline, OR pass a `storagePath`
  // ("<bucket>/<key>") and let the edge function fetch the bytes from
  // Supabase Storage with its service role. Storage path is strongly
  // preferred for >50 KB files: it avoids inflating the JSON request body
  // with base64 PDF data and survives slow uploads.
  content?: string;
  storagePath?: string;
  contentType: string;
  encoding?: 'base64' | 'text';
}

// The edge function ACKs at 202 immediately and continues SMTP work in the
// background via EdgeRuntime.waitUntil. With storagePath attachments the
// JSON body is tiny (~5 KB), so 60 s is far more than enough even on bad
// uplinks. The actual SMTP outcome is recorded on the reservation row's
// email_sent_at / email_error columns.
const SEND_EMAIL_TIMEOUT_MS = 60_000;

async function sendB2BEmail(
  to: string,
  subject: string,
  html: string,
  attachments?: EmailAttachment[],
  reservationId?: string,
  pdfCcEmails?: string[],
  adminId?: string,
): Promise<boolean> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  if (!supabaseUrl) {
    throw new Error('Supabase configuration not found');
  }

  const authToken = localStorage.getItem('auth_token');
  if (!authToken) {
    throw new Error('Not authenticated — please log in again before sending email');
  }

  const body: Record<string, unknown> = { to, subject, html };
  if (pdfCcEmails && pdfCcEmails.length > 0) {
    body.cc = pdfCcEmails;
  }
  if (attachments && attachments.length > 0) {
    // Map our camelCase frontend type to the snake_case shape the edge
    // function expects (storage_path).
    body.attachments = attachments.map(a => ({
      filename: a.filename,
      contentType: a.contentType,
      ...(a.content !== undefined ? { content: a.content } : {}),
      ...(a.storagePath ? { storage_path: a.storagePath } : {}),
      ...(a.encoding ? { encoding: a.encoding } : {}),
    }));
  }
  if (reservationId) {
    body.reservation_id = reservationId;
  }
  if (adminId) {
    // Lets the edge function look up admin_brevo_credentials and prefer
    // Brevo (proper DKIM) over raw Strato SMTP for reliable Gmail delivery.
    body.admin_id = adminId;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SEND_EMAIL_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
      method: 'POST',
      headers: await getValidAuthHeadersOrThrow(),
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error(`Email request timed out after ${SEND_EMAIL_TIMEOUT_MS / 1000}s (request never reached edge function)`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as Record<string, string>).details || 'Failed to queue email');
  }

  return true;
}

export async function sendEdgeEmail(opts: {
  to: string;
  subject: string;
  html: string;
  attachments?: EmailAttachment[];
  reservationId?: string;
  cc?: string[];
  adminId?: string;
}): Promise<boolean> {
  return sendB2BEmail(
    opts.to,
    opts.subject,
    opts.html,
    opts.attachments,
    opts.reservationId,
    opts.cc,
    opts.adminId,
  );
}

function formatCurrency(amount: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('en-EU', { style: 'currency', currency }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

export async function sendB2BReservationConfirmation(
  buyer: B2BBuyer,
  reservation: B2BReservation,
  items: ReservationItemForEmail[],
  supplier: SupplierInfo,
  pickupDate: string,
  attachments?: EmailAttachment[],
  reservationId?: string,
  pdfCcEmails?: string[],
  adminId?: string,
): Promise<boolean> {
  const currency = buyer.preferred_currency || 'EUR';
  const itemRows = items.map(item => `
    <tr>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${item.name}${item.sku ? ` (${item.sku})` : ''}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">${item.quantity}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatCurrency(item.unit_price, currency)}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatCurrency(item.total_price, currency)}</td>
    </tr>
  `).join('');

  const html = `
    <div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;color:#1f2937;">
      <div style="background:#1e40af;color:white;padding:24px 32px;border-radius:8px 8px 0 0;">
        <h1 style="margin:0;font-size:22px;">Reservation Confirmation</h1>
        <p style="margin:8px 0 0;opacity:0.9;font-size:14px;">Reservation #${reservation.reservation_number}</p>
      </div>
      <div style="padding:32px;background:#ffffff;border:1px solid #e5e7eb;border-top:none;">
        <p style="margin:0 0 16px;">Dear ${buyer.contact_person_name},</p>
        <p style="margin:0 0 24px;">Your reservation has been submitted successfully. Below are the details:</p>

        <div style="background:#f9fafb;border-radius:8px;padding:16px;margin-bottom:24px;">
          <table style="width:100%;font-size:14px;">
            <tr><td style="padding:4px 0;color:#6b7280;">Reservation Number:</td><td style="padding:4px 0;font-weight:600;">${reservation.reservation_number}</td></tr>
            <tr><td style="padding:4px 0;color:#6b7280;">Date:</td><td style="padding:4px 0;">${formatDate(reservation.created_at)}</td></tr>
            <tr><td style="padding:4px 0;color:#6b7280;">Scheduled Pickup:</td><td style="padding:4px 0;font-weight:600;color:#1e40af;">${formatDate(pickupDate)}</td></tr>
            <tr><td style="padding:4px 0;color:#6b7280;">Company:</td><td style="padding:4px 0;">${buyer.trade_name}</td></tr>
          </table>
        </div>

        <h3 style="margin:0 0 12px;font-size:16px;color:#111827;">Reserved Items</h3>
        <table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:16px;">
          <thead>
            <tr style="background:#f3f4f6;">
              <th style="padding:8px 12px;text-align:left;font-weight:600;">Item</th>
              <th style="padding:8px 12px;text-align:center;font-weight:600;">Qty</th>
              <th style="padding:8px 12px;text-align:right;font-weight:600;">Unit Price</th>
              <th style="padding:8px 12px;text-align:right;font-weight:600;">Total</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
          <tfoot>
            <tr style="font-weight:700;font-size:15px;">
              <td colspan="3" style="padding:12px;text-align:right;border-top:2px solid #1e40af;">Total:</td>
              <td style="padding:12px;text-align:right;border-top:2px solid #1e40af;">${formatCurrency(reservation.total_amount, currency)}</td>
            </tr>
          </tfoot>
        </table>

        <div style="background:#eff6ff;border-left:4px solid #1e40af;padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:16px;">
          <p style="margin:0;font-size:14px;color:#1e40af;">You will receive a notification when your order is ready for pickup.</p>
        </div>

        <div style="background:#ecfdf5;border-left:4px solid #059669;padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:24px;">
          <p style="margin:0;font-size:14px;color:#065f46;">Your invoice is attached as a PDF. You can also download it any time from the B2B portal under <strong>Invoices</strong>.</p>
        </div>

        <div style="background:#fef3c7;border-radius:6px;padding:10px 16px;margin-bottom:16px;">
          <p style="margin:0;font-size:12px;color:#92400e;">This is an automatic confirmation email — please do not reply.</p>
        </div>

        <div style="border-top:1px solid #e5e7eb;padding-top:16px;margin-top:16px;">
          <p style="margin:0;font-size:12px;color:#9ca3af;">
            ${supplier.company_name || ''}${supplier.company_address ? ` | ${supplier.company_address}` : ''}${supplier.vat_number ? ` | VAT: ${supplier.vat_number}` : ''}
          </p>
        </div>
      </div>
    </div>
  `;

  return sendB2BEmail(
    buyer.email,
    `Reservation Confirmation #${reservation.reservation_number}`,
    html,
    attachments,
    reservationId ?? reservation.id,
    pdfCcEmails,
    adminId ?? buyer.admin_id,
  );
}

export async function sendB2BReadyNotification(
  buyer: B2BBuyer,
  reservation: B2BReservation,
  supplier: SupplierInfo
): Promise<boolean> {
  const pickupDate = reservation.pickup_scheduled_date
    ? formatDate(reservation.pickup_scheduled_date)
    : 'To be confirmed';

  const html = `
    <div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;color:#1f2937;">
      <div style="background:#059669;color:white;padding:24px 32px;border-radius:8px 8px 0 0;">
        <h1 style="margin:0;font-size:22px;">Your Order is Ready for Pickup</h1>
        <p style="margin:8px 0 0;opacity:0.9;font-size:14px;">Reservation #${reservation.reservation_number}</p>
      </div>
      <div style="padding:32px;background:#ffffff;border:1px solid #e5e7eb;border-top:none;">
        <p style="margin:0 0 16px;">Dear ${buyer.contact_person_name},</p>
        <p style="margin:0 0 24px;">Your reservation <strong>#${reservation.reservation_number}</strong> has been prepared and is ready for pickup.</p>

        <div style="background:#ecfdf5;border-radius:8px;padding:20px;margin-bottom:24px;text-align:center;">
          <p style="margin:0 0 8px;font-size:14px;color:#065f46;">Scheduled Pickup Date</p>
          <p style="margin:0;font-size:24px;font-weight:700;color:#059669;">${pickupDate}</p>
        </div>

        <div style="background:#f9fafb;border-radius:8px;padding:16px;margin-bottom:24px;">
          <p style="margin:0 0 8px;font-weight:600;font-size:14px;">Contact Information</p>
          <p style="margin:0;font-size:14px;color:#6b7280;">
            ${supplier.company_name || ''}${supplier.company_phone ? `<br>Phone: ${supplier.company_phone}` : ''}${supplier.company_email ? `<br>Email: ${supplier.company_email}` : ''}${supplier.company_address ? `<br>Address: ${supplier.company_address}` : ''}
          </p>
        </div>

        <div style="border-top:1px solid #e5e7eb;padding-top:16px;margin-top:16px;">
          <p style="margin:0;font-size:12px;color:#9ca3af;">
            ${supplier.company_name || ''}${supplier.vat_number ? ` | VAT: ${supplier.vat_number}` : ''}
          </p>
        </div>
      </div>
    </div>
  `;

  return sendB2BEmail(
    buyer.email,
    `Order Ready for Pickup - #${reservation.reservation_number}`,
    html,
    undefined,
    reservation.id,
    undefined,
    undefined,
  );
}

interface InvoiceForEmail {
  invoice_number: string;
  total_amount: number;
}

// Sends an "updated invoice" email after an admin edits a B2B invoice. Distinct
// wording/subject from the reservation confirmation; reuses the same transport.
export async function sendB2BInvoiceUpdatedEmail(
  buyer: B2BBuyer,
  invoice: InvoiceForEmail,
  supplier: SupplierInfo,
  attachments?: EmailAttachment[],
  ccEmails?: string[],
  adminId?: string,
  reservationId?: string,
): Promise<boolean> {
  const currency = buyer.preferred_currency || 'EUR';

  const html = `
    <div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;color:#1f2937;">
      <div style="background:#1e40af;color:white;padding:24px 32px;border-radius:8px 8px 0 0;">
        <h1 style="margin:0;font-size:22px;">Updated Invoice</h1>
        <p style="margin:8px 0 0;opacity:0.9;font-size:14px;">Invoice #${invoice.invoice_number}</p>
      </div>
      <div style="padding:32px;background:#ffffff;border:1px solid #e5e7eb;border-top:none;">
        <p style="margin:0 0 16px;">Dear ${buyer.contact_person_name},</p>
        <p style="margin:0 0 24px;">Your invoice <strong>#${invoice.invoice_number}</strong> has been updated. The latest version is attached as a PDF, and you can always download the current copy from the B2B portal under <strong>Invoices</strong>.</p>

        <div style="background:#f9fafb;border-radius:8px;padding:16px;margin-bottom:24px;">
          <table style="width:100%;font-size:14px;">
            <tr><td style="padding:4px 0;color:#6b7280;">Invoice Number:</td><td style="padding:4px 0;font-weight:600;">${invoice.invoice_number}</td></tr>
            <tr><td style="padding:4px 0;color:#6b7280;">New Total:</td><td style="padding:4px 0;font-weight:700;color:#1e40af;">${formatCurrency(invoice.total_amount, currency)}</td></tr>
            <tr><td style="padding:4px 0;color:#6b7280;">Company:</td><td style="padding:4px 0;">${buyer.trade_name}</td></tr>
          </table>
        </div>

        <div style="background:#fef3c7;border-radius:6px;padding:10px 16px;margin-bottom:16px;">
          <p style="margin:0;font-size:12px;color:#92400e;">This is an automatic notification — please do not reply.</p>
        </div>

        <div style="border-top:1px solid #e5e7eb;padding-top:16px;margin-top:16px;">
          <p style="margin:0;font-size:12px;color:#9ca3af;">
            ${supplier.company_name || ''}${supplier.company_address ? ` | ${supplier.company_address}` : ''}${supplier.vat_number ? ` | VAT: ${supplier.vat_number}` : ''}
          </p>
        </div>
      </div>
    </div>
  `;

  return sendB2BEmail(
    buyer.email,
    `Updated invoice #${invoice.invoice_number}`,
    html,
    attachments,
    reservationId,
    ccEmails,
    adminId ?? buyer.admin_id,
  );
}
